import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * DeployOllamaModal — install/upgrade Ollama on a registered host (P1-AI07).
 *
 * Two endpoint shape:
 *   POST  /v1/admin/ai-hosts/:id/ollama/install         { force? }
 *   GET   /v1/admin/ai-hosts/:id/ollama/install/status  → { active, job }
 *
 * The install job runs server-side via SSH (bash on mac/linux, powershell
 * on windows — see installer.ts). The router returns 202 immediately
 * with the initial job state; we then poll status every 2s while the
 * job is RUNNING, then stop polling and show the final result.
 *
 * Mirror of K-Stack-Products/Gateway/local-gateway/admin-ui/src/components/DeployOllamaModal.tsx
 * in spirit — same scrolling log tail + force-reinstall checkbox.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../../api.js';
const POLL_INTERVAL_MS = 2000;
export function DeployOllamaModal({ host, onClose, onToast, }) {
    const fetcher = useNexusFetch();
    const [job, setJob] = useState(null);
    const [starting, setStarting] = useState(false);
    const [force, setForce] = useState(false);
    const [error, setError] = useState(null);
    const logRef = useRef(null);
    // Auto-scroll the log to the bottom as new lines arrive.
    useEffect(() => {
        const el = logRef.current;
        if (!el)
            return;
        el.scrollTop = el.scrollHeight;
    }, [job?.logs]);
    // On mount, check if there's already an active job for this host.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(host.id)}/ollama/install/status`);
                if (cancelled)
                    return;
                if (res.active && res.job) {
                    setJob(res.job);
                }
            }
            catch {
                // Status probe failure is non-fatal; user can still kick a new install.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fetcher, host.id]);
    // Poll while the job is running.
    useEffect(() => {
        if (!job || job.status !== 'running')
            return undefined;
        let cancelled = false;
        const id = window.setInterval(async () => {
            try {
                const res = await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(host.id)}/ollama/install/status`);
                if (cancelled)
                    return;
                if (res.job) {
                    setJob(res.job);
                    if (res.job.status !== 'running') {
                        window.clearInterval(id);
                        if (res.job.status === 'success') {
                            onToast('success', `Ollama install on ${host.name} finished ok.`);
                        }
                        else {
                            onToast('danger', `Ollama install on ${host.name} failed: ${res.job.error ?? '(no detail)'}`);
                        }
                    }
                }
            }
            catch (e) {
                // Network blip — keep polling.
                if (cancelled)
                    return;
                console.warn('install status poll failed', e);
            }
        }, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [fetcher, host.id, host.name, job, onToast]);
    const handleStart = useCallback(async () => {
        setStarting(true);
        setError(null);
        try {
            const res = await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(host.id)}/ollama/install`, { method: 'POST', json: { force } });
            setJob(res.job);
            if (res.alreadyRunning) {
                onToast('info', `Install for ${host.name} is already running.`);
            }
            else {
                onToast('info', `Install kicked for ${host.name}.`);
            }
        }
        catch (e) {
            const msg = e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : 'install kick failed';
            setError(msg);
            onToast('danger', msg);
        }
        finally {
            setStarting(false);
        }
    }, [fetcher, force, host.id, host.name, onToast]);
    const running = job?.status === 'running';
    const finished = job?.status === 'success' || job?.status === 'error';
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal show", style: { display: 'block' }, role: "dialog", "aria-modal": "true", children: _jsx("div", { className: "modal-dialog modal-dialog-centered modal-lg", children: _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-header", children: [_jsxs("h5", { className: "modal-title", children: ["Install / upgrade Ollama on ", _jsx("code", { children: host.name })] }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: onClose, disabled: running })] }), _jsxs("div", { className: "modal-body", children: [_jsxs("p", { className: "small text-muted mb-3", children: ["Dispatches over SSH to the host's ", _jsx("code", { children: host.os }), " shell (", host.os === 'windows' ? 'powershell + winget' : 'bash + brew/curl install.sh', "). The install script is idempotent \u2014 skips if Ollama is already on PATH unless ", _jsx("em", { children: "force" }), " is set."] }), _jsxs("div", { className: "form-check mb-3", children: [_jsx("input", { type: "checkbox", className: "form-check-input", id: "ollama-install-force", checked: force, onChange: (e) => setForce(e.target.checked), disabled: running || starting }), _jsxs("label", { className: "form-check-label small", htmlFor: "ollama-install-force", children: ["Force re-install even if ", _jsx("code", { children: "ollama" }), " is already on PATH."] })] }), error && _jsx("div", { className: "alert alert-danger", children: error }), job && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "d-flex gap-2 align-items-center mb-2 small", children: [_jsxs("span", { children: ["Status:", ' ', _jsxs("strong", { children: [job.status === 'running' && (_jsx("span", { className: "badge bg-info text-dark", children: "running" })), job.status === 'success' && (_jsx("span", { className: "badge bg-success", children: "success" })), job.status === 'error' && (_jsx("span", { className: "badge bg-danger", children: "error" }))] })] }), _jsxs("span", { className: "text-muted", children: ["mode: ", _jsx("code", { children: job.mode })] }), job.exitCode !== null && (_jsxs("span", { className: "text-muted", children: ["exit: ", _jsx("code", { children: job.exitCode })] }))] }), _jsx("pre", { ref: logRef, className: "bg-dark text-light small p-2 rounded mb-2", style: { maxHeight: 260, overflowY: 'auto' }, children: job.logs.length === 0
                                                    ? '(no output yet)'
                                                    : job.logs.join('\n') }), job.error && (_jsxs("div", { className: "alert alert-danger small", children: [_jsx("strong", { children: "Error:" }), " ", job.error] }))] }))] }), _jsxs("div", { className: "modal-footer", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: onClose, disabled: running, children: finished ? 'Close' : 'Cancel' }), _jsx("button", { type: "button", className: "btn btn-primary", onClick: () => void handleStart(), disabled: starting || running, children: starting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Kicking\u2026"] })) : running ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Installing\u2026"] })) : finished ? ('Run again') : ('Install') })] })] }) }) }), _jsx("div", { className: "modal-backdrop show" })] }));
}
//# sourceMappingURL=DeployOllamaModal.js.map